import { create } from 'zustand';
import api from '../services/api';

// 支付提供商类型
export type PaymentProvider = 'wechatpay' | 'alipay' | 'stripe' | 'creem' | 'paddle' | 'lemonsqueezy';

// 存储提供商类型
export type StorageProvider = 'local' | 'r2' | 's3' | 'qiniu';

// 运行时配置接口
export interface RuntimeConfig {
  region: string;
  domain: string;
  defaultLocale: string;
  supportedLocales: string[];
  loadingSite: string;
  featureFlags: Record<string, boolean>;
  supportedStorageProviders: StorageProvider[];
  supportedPaymentProviders: PaymentProvider[];
  /** 银行卡对账单展示名（服务端 [payment] billingDescriptor） */
  billingDescriptor?: string;
  /** 客服/帮助中心链接（服务端 [payment] supportUrl） */
  supportUrl?: string;
}

// 运行时配置状态接口
interface RuntimeConfigState {
  config: RuntimeConfig | null;
  isLoading: boolean;
  error: string | null;
  
  // Actions
  fetchConfig: () => Promise<void>;
  
  // Getters
  isRegionCN: () => boolean;
  isRegionGlobal: () => boolean;
  isStorageProviderSupported: (provider: StorageProvider) => boolean;
  isPaymentProviderSupported: (provider: PaymentProvider) => boolean;
  getSupportedStorageProviders: () => StorageProvider[];
  getSupportedPaymentProviders: () => PaymentProvider[];
  isFeatureEnabled: (feature: string) => boolean;
}

// 默认配置（用于错误情况）
const defaultConfig: RuntimeConfig = {
  region: 'cn',
  domain: 'biaogeliu.com',
  defaultLocale: 'zh-CN',
  supportedLocales: ['zh-CN'],
  loadingSite: 'website',
  featureFlags: {
    wechat_login: true,
    wechat_pay: false,
    google_login: false,
    stripe: false,
    creem: false,
    paddle: false,
    lemonsqueezy: false,
  },
  supportedStorageProviders: ['qiniu', 'local'],
  supportedPaymentProviders: [],
};

export const useRuntimeConfigStore = create<RuntimeConfigState>((set, get) => ({
  config: null,
  isLoading: false,
  error: null,

  fetchConfig: async () => {
    // 如果已经加载过，不再重复请求
    if (get().config !== null) {
      return;
    }

    set({ isLoading: true, error: null });
    try {
      const response = await api.get<RuntimeConfig>('/runtime-config');
      if (response.code === 0 && response.data) {
        set({ config: response.data, isLoading: false });
      } else {
        console.warn('[RuntimeConfig] Failed to fetch config, using default');
        set({ config: defaultConfig, isLoading: false });
      }
    } catch (error) {
      console.error('[RuntimeConfig] Error fetching config:', error);
      set({ config: defaultConfig, isLoading: false, error: 'Failed to fetch runtime config' });
    }
  },

  isRegionCN: () => {
    const { config } = get();
    return config?.region === 'cn';
  },

  isRegionGlobal: () => {
    const { config } = get();
    return config?.region === 'global';
  },

  isStorageProviderSupported: (provider: StorageProvider) => {
    const { config } = get();
    if (!config) return false;
    return config.supportedStorageProviders.includes(provider);
  },

  isPaymentProviderSupported: (provider: PaymentProvider) => {
    const { config } = get();
    if (!config) return false;
    return config.supportedPaymentProviders.includes(provider);
  },

  getSupportedStorageProviders: () => {
    const { config } = get();
    return config?.supportedStorageProviders || [];
  },

  getSupportedPaymentProviders: () => {
    const { config } = get();
    return config?.supportedPaymentProviders || [];
  },

  isFeatureEnabled: (feature: string) => {
    const { config } = get();
    if (!config) return false;
    return config.featureFlags[feature] || false;
  },
}));
